import { RCXLogger, RecordType } from 'rcx_adapter';

import { Authorize } from './Authorize';
import { Token } from './Token';
import { tableNameMap } from '../constants';
import { ServiceNowEntity } from '../enums';

interface DataFetchOptions {
    domain: string;
}

export const getOptions = (params: { [k: string]: any }, exactMatch?: boolean, limit?: number) => {
    return {
        sysparm_query: Object.keys(params)
            .reduce((pre: string[], key) => {
                pre.push(`${key}${exactMatch ? '=' : 'LIKE'}${params[key]}`);
                return pre;
            }, [])
            .join('^OR'),
        sysparm_limit: limit || 50,
    };
};

export class DataFetch {
    private token: Token = new Token();
    private authorize: Authorize = new Authorize({});
    private domain: string;
    constructor({ domain }: DataFetchOptions) {
        this.domain = domain;
    }
    public async _fetch(method: string, url: string, options?: any): Promise<any> {
        const result = await fetch(this.domain + url, {
            method,
            headers: {
                Authorization: `Bearer ${this.token.accessToken()}`,
                'Content-Type': 'application/json;charset=utf-8',
            },
            body: options && JSON.stringify(options),
        });
        if (result.status !== 200) {
            throw new Error('Failed to fetch data');
        }
        const data = await result.json();
        return data;
    }
    private fetchApi(method: string, pathname: string, options?: any): Promise<any> {
        let url = pathname;
        let params = options;
        if (method === 'GET' && options) {
            url = pathname + '?' + new URLSearchParams(options).toString();
            params = undefined;
        }
        return new Promise((resolve, reject) => {
            this._fetch(method, url, params)
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => {
                    this.authorize.refresh().then((success) => {
                        if (success) {
                            this._fetch(method, url, params)
                                .then((data) => {
                                    resolve(data);
                                })
                                .catch((err) => {
                                    RCXLogger.getInstance().error(
                                        `[ServiceNow][fetchApi] retry error. URL: ${url},params: ${JSON.stringify(
                                            params
                                        )}`,
                                        err
                                    );
                                    reject(err);
                                });
                        } else {
                            RCXLogger.getInstance().error(
                                `[ServiceNow][fetchApi] refresh token error. URL: ${url},params: ${JSON.stringify(
                                    params
                                )}`,
                                error
                            );
                            reject(error);
                        }
                    });
                });
        });
    }
    private _get(pathname: string, options?: { [k: string]: any }): Promise<any> {
        return this.fetchApi('GET', pathname, options);
    }

    private _patch(pathname: string, args?: { [k: string]: any }): Promise<any> {
        return this.fetchApi('PATCH', pathname, args);
    }

    private _post(pathname: string, args?: { [k: string]: any }): Promise<any> {
        return this.fetchApi('POST', pathname, args);
    }

    private _put(pathname: string, args?: { [k: string]: any }): Promise<any> {
        return this.fetchApi('PUT', pathname, args);
    }

    public getTableData(key: RecordType, params: { [k: string]: any }) {
        const tableName = tableNameMap[key as keyof typeof tableNameMap];
        if (!tableName) {
            return Promise.reject(new Error(`Table not found: ${key}`));
        }

        return this._get(`api/now/table/${tableName}`, getOptions(params));
    }

    public getInteractionByCallIds(ids: string[]) {
        return this._get('api/now/table/interaction', {
            sysparm_query: ids.map((id) => `u_call_id=${id}`).join('^OR'),
        });
    }
    public updateInteraction(id: string, args: { [k: string]: any }) {
        return this._patch(`api/now/table/interaction/${id}`, args);
    }
    public getVersion() {
        return this._get('api/now/table/sys_properties', { name: 'glide.war' });
    }
    public getRingCXForServiceNowConfig() {
        return this._get(`api/now/table/${ServiceNowEntity.RINGCX_FOR_SERVICENOW_CONFIG}`, {});
    }

    public getIncidentByCallIds(ids: string[]) {
        return this._get('api/now/table/incident', {
            sysparm_query: ids.map((id) => `commentsLIKE${id}`).join('^OR'),
            sysparm_display_value: 'true',
        });
    }
    public getIncidentComments(id: string) {
        return this._get('api/now/table/incident', {
            sysparm_query: `sys_id=${id}`,
            sysparm_display_value: 'true',
            sysparm_fields: 'comments',
        });
    }
    public updateIncident(id: string, args: { [k: string]: any }) {
        return this._patch(`api/now/table/incident/${id}`, args);
    }
    public updatePresenceState(id: string, stateId: string) {
        const stateIds = stateId.split(',');
        const param = {
            presence: {
                sys_id: stateIds[0],
                channels: [], // Initialize channels as an empty object
            },
        };
        if (stateIds.length > 1) {
            (param.presence.channels as Array<{ available: boolean; sys_id: string }>) = [
                {
                    available: true,
                    sys_id: stateIds[1],
                },
            ];
        }
        return this._put(`api/now/awa/agents/${id}`, param);
    }
    public getPresenceState(id: string) {
        return this._get(`api/now/awa/agents/${id}`);
    }
    public getStatesSyncMap() {
        return this._get('api/now/table/u_ringcx_servicenow_states_sync');
    }
    public getPresenceStates() {
        return this._get('api/now/table/awa_presence_state');
    }
    public getServiceChannel() {
        return this._get('api/now/table/awa_service_channel');
    }
}
